import React, { useEffect, useState, useMemo, useContext } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TableSortLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { collection, query, onSnapshot, deleteDoc, doc, addDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import ConfirmationDialog from './ConfirmationDialog';
import InfoModalV2 from '../components/InfoModalV2';
import { createTraySchema } from '../modals/TraySchema';
import useIsMobile from '../utils/useIsMobile';
import { AppContext } from '../AppContext';
import AddTray from './AddTray';

function Trays({ setError, setSuccess }) {
  const { user, userData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const isMobile = useIsMobile();
  const [trays, setTrays] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [trayToEdit, setTrayToEdit] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedTray, setSelectedTray] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTray, setConfirmTray] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // New: InfoModal state
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedTrayData, setSelectedTrayData] = useState(null);

  const [traysSortColumn, setTraysSortColumn] = useState('brand');
  const [traysSortDirection, setTraysSortDirection] = useState('asc');

  useEffect(() => {
    if (!user || !activeFarmId) {
      setTrays([]);
      return;
    }
    const traysRef = collection(db, 'farms', activeFarmId, 'trays');
    const q = query(traysRef);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setTrays(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return unsubscribe;
  }, [user, activeFarmId]);

  const filteredTrays = useMemo(() => {
    return trays.filter((tray) =>
      tray.brand.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [trays, searchTerm]);

  const getSizeString = (tray) => {
    if (tray.length && tray.width && tray.lengthUnit && tray.widthUnit) {
      return `${tray.length}${tray.lengthUnit} x ${tray.width}${tray.widthUnit}`;
    }
    return 'N/A';
  };

  const getPriceString = (tray) => {
    if (!tray.price || isNaN(parseFloat(tray.price))) return 'N/A';
    return tray.priceCurrency ? `${tray.priceCurrency}${tray.price}` : tray.price;
  };

  const sortedTrays = useMemo(() => {
    const sorted = [...filteredTrays].sort((a, b) => {
      let aValue, bValue;
      switch (traysSortColumn) {
        case 'brand':
          aValue = a.brand ? a.brand.toLowerCase() : '';
          bValue = b.brand ? b.brand.toLowerCase() : '';
          break;
        case 'stock':
          aValue = Number(a.stock) || 0;
          bValue = Number(b.stock) || 0;
          break;
        case 'size':
          aValue = getSizeString(a);
          bValue = getSizeString(b);
          break;
        case 'price':
          aValue = parseFloat(a.price) || 0;
          bValue = parseFloat(b.price) || 0;
          break;
        default:
          aValue = '';
          bValue = '';
      }
      if (aValue < bValue) return traysSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return traysSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredTrays, traysSortColumn, traysSortDirection]);

  const paginatedTrays = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedTrays.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedTrays, page, rowsPerPage]);

  const handleTraysSort = (column) => {
    if (traysSortColumn === column) {
      setTraysSortDirection(traysSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setTraysSortColumn(column);
      setTraysSortDirection('asc');
    }
  };

  const handleOpen = () => setOpen(true);
  const handleCloseDialog = () => {
    setOpen(false);
    setEditMode(false);
    setTrayToEdit(null);
  };
  const handleAddTray = () => {
    handleCloseDialog();
  };

  const handleClone = async () => {
    if (!selectedTray || !user) return;
    if (!activeFarmId) {
      setError?.('No active farm selected.');
      return;
    }
    try {
      const { id, ...dataToClone } = selectedTray;
      await addDoc(collection(db, 'farms', activeFarmId, 'trays'), dataToClone);
      setSuccess?.('Tray cloned successfully')
    } catch (err) {
      setError?.('Failed to clone tray.');
    }
    handleMenuClose();
  };

  const handleDelete = async (id) => {
    if (!user) return;
    if (!activeFarmId) {
      setError?.('No active farm selected.');
      return;
    }
    try {
      await deleteDoc(doc(db, 'farms', activeFarmId, 'trays', id));
      setSuccess?.('Tray deleted successfully');
    } catch (error) {
      console.error('Error deleting tray:', error);
      setError?.('Failed to delete tray');
    }
    handleMenuClose();
  };

  const openConfirmDialog = (tray) => {
    setConfirmTray(tray);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (confirmTray) {
      await handleDelete(confirmTray.id);
    }
    setConfirmOpen(false);
    setConfirmTray(null);
  };

  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    setConfirmTray(null);
  };

  const handleMenuOpen = (event, tray) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedTray(tray);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedTray(null);
  };

  const handleEdit = () => {
    setEditMode(true);
    setTrayToEdit(selectedTray);
    handleOpen();
    handleMenuClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // NEW: Modal open on row click
  const handleRowClick = (tray) => {
    setSelectedTrayData(tray);
    setInfoModalOpen(true);
  };

  const traySchema = useMemo(() => createTraySchema({ getSizeString, getPriceString }), [getSizeString, getPriceString]);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
    '& .MuiTableSortLabel-root': {
      color: `${theme.palette.common.white} !important`,
      '&.Mui-active': {
        color: `${theme.palette.common.white} !important`,
      },
      '& .MuiTableSortLabel-icon': {
        color: `${theme.palette.common.white} !important`,
      },
      '&:hover': {
        color: 'rgba(255, 255, 255, 0.75) !important',
      },
    },
  }));

  const subscriptionActive = isSubscriptionActive(userData);

  return (
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        mt: '20px',
        maxWidth: { xs: '100%', sm: '100%', md: '80%'},
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Trays</Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleOpen}
          disabled={!subscriptionActive}
        >
          Add Tray
        </Button>
      </Box>
      {isMobile && (
        <Paper sx={{ mb: 2 }}>
          <List disablePadding>
            {paginatedTrays.map((tray) => (
              <React.Fragment key={tray.id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleRowClick(tray)} sx={{ alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2">{tray.brand}</Typography>
                          <Chip size="small" color="info" label={getSizeString(tray)} />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Stock: {tray.stock ?? '—'}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            {getPriceString(tray)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
            {paginatedTrays.length === 0 && (
              <ListItem>
                <ListItemText primary="No trays found." />
              </ListItem>
            )}
          </List>
        </Paper>
      )}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <TableSortLabel
                  active={traysSortColumn === 'brand'}
                  direction={traysSortColumn === 'brand' ? traysSortDirection : 'asc'}
                  onClick={() => handleTraysSort('brand')}
                >
                  Brand
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={traysSortColumn === 'stock'}
                  direction={traysSortColumn === 'stock' ? traysSortDirection : 'asc'}
                  onClick={() => handleTraysSort('stock')}
                >
                  Stock
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={traysSortColumn === 'size'}
                  direction={traysSortColumn === 'size' ? traysSortDirection : 'asc'}
                  onClick={() => handleTraysSort('size')}
                >
                  Size
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={traysSortColumn === 'price'}
                  direction={traysSortColumn === 'price' ? traysSortDirection : 'asc'}
                  onClick={() => handleTraysSort('price')}
                >
                  Price
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Notes</span>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Mouse over note to read entire entry.
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTrays.map((tray) => (
              <TableRow key={tray.id} onClick={() => handleRowClick(tray)} sx={{ cursor: 'pointer' }}>
                <TableCell>{tray.brand}</TableCell>
                <TableCell>{tray.stock}</TableCell>
                <TableCell>{getSizeString(tray)}</TableCell>
                <TableCell>{getPriceString(tray)}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 100,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Tooltip title={<Typography sx={{ fontSize: '0.8rem' }}>{tray.notes || ''}</Typography>}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {tray.notes || ''}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <IconButton onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, tray); }}>
                    <SettingsIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedTrays.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No trays found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredTrays.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem disabled={!subscriptionActive} onClick={handleEdit}>Edit</MenuItem>
        <MenuItem disabled={!subscriptionActive} onClick={handleClone}>Clone</MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); openConfirmDialog(selectedTray); }}>
          Delete
        </MenuItem>
      </Menu>
      <AddTray
        open={open}
        onClose={handleCloseDialog}
        onAdd={handleAddTray}
        editMode={editMode}
        trayToEdit={trayToEdit}
        setSuccess={setSuccess}
        setError={setError}
      />

      <ConfirmationDialog
        open={confirmOpen}
        title="Confirm Tray Deletion"
        message="Are you sure you want to delete this tray?"
        onConfirm={handleConfirmDelete}
        onClose={handleConfirmCancel}
      />
      <InfoModalV2
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        data={selectedTrayData}
        schema={traySchema}
      />
    </Box>
  );
}

export default Trays;

function isSubscriptionActive(userData) {
  if (!userData || !userData.accessExpires) return true;
  const now = new Date();
  const expires = userData.accessExpires.toDate
    ? userData.accessExpires.toDate()
    : new Date(userData.accessExpires);
  return now <= expires;
}


